import { Form, Formik } from "formik"
import { useMemo, useState } from "react"
import { Badge, Col, Row } from "react-bootstrap"
import { useTranslation } from "react-i18next"

import Button from "@/components/Button/Button"
import TextInput from "@/components/Inputs/TextInput"
import { MODALS } from "@/constants/modals"
import { useAddSkillMutation } from "@/queries/mutations/skillMutations"
import { deleteIcon, plusIcon } from "@/utils/variables"
import { useStore } from "@/zustand/store"

export default function SkillsModal() {
  const { t } = useTranslation()
  const [error, setError] = useState("")
  const skills = useStore((state) => state.modalsStates[MODALS.skills.edit])
  const editModalState = useStore((state) => state.editModalState)

  const addSkillMutation = useAddSkillMutation()

  const initialValues = useMemo(() => {
    if (!skills) return { skills: [], search: "" }
    return {
      skills: skills?.map?.((s) =>
        typeof s === "string" ? { title: s, is_course_skill: true } : s
      ),
      search: "",
    }
  }, [skills])

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => {
        const final = {
          title: values.skills
            ?.filter?.((s) => s.is_course_skill === false)
            ?.map?.((s) => s.title),
        }
        addSkillMutation.mutate(final, {
          onSuccess: () => editModalState(MODALS.skills.edit, undefined),
        })
      }}
    >
      {({ values, setFieldValue, handleChange }) => {
        const addSkill = () => {
          const newSkill = values.search.trim()
          if (!newSkill) return

          if (values.skills?.some?.((s) => s.title === newSkill)) {
            setError(t("skillsModal.errors.exists"))
          } else {
            setFieldValue("skills", [
              ...values.skills,
              { title: newSkill, is_course_skill: false },
            ])
            setFieldValue("search", "")
            setError("") // clear error on success
          }
        }

        const removeSkill = (title: string) => {
          setFieldValue(
            "skills",
            values.skills?.filter?.((s) => s.title !== title)
          )
        }

        return (
          <Form>
            <Row className="align-items-center mb-2">
              <Col xs="10">
                <TextInput
                  id="search"
                  label=""
                  name="search"
                  placeholder={t("skillsModal.data.skill.placeholder")}
                  value={values.search}
                  onChange={handleChange}
                />
                {error && <div className="text-danger small mt-1">{error}</div>}
              </Col>
              <Col xs="2">
                <Button
                  variant="secondary"
                  className="w-100 d-flex justify-content-center align-items-center"
                  type="button"
                  disabled={!values.search?.trim()}
                  onClick={addSkill}
                >
                  {plusIcon}
                  {t("buttons.add")}
                </Button>
              </Col>
            </Row>

            {values.skills?.length === 0 && (
              <div className="text-muted text-center">
                {t("skillsModal.data.noData")}
              </div>
            )}

            <div className="d-flex flex-wrap gap-2">
              {values.skills?.map?.((skill, idx) => (
                <Badge
                  bg={skill.is_course_skill ? "secondary" : "primary"}
                  key={idx}
                  className="d-flex align-items-center px-3 py-2 fs-6 fw-light rounded-pill"
                >
                  {skill.title}
                  {!skill.is_course_skill && (
                    <Button
                      onClick={() => removeSkill(skill.title)}
                      className="ms-2 p-0 text-gray fw-bold bg-transparent"
                    >
                      {deleteIcon}
                    </Button>
                  )}
                </Badge>
              ))}
            </div>

            <div className="d-flex justify-content-end gap-3 mt-4 mb-2 align-items-center">
              <Button
                isLoading={addSkillMutation.isPending}
                disabled={values.skills?.length === 0}
                type="submit"
              >
                {plusIcon} {t("buttons.save")}
              </Button>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}
